import React, { useState, useEffect } from 'react';
import { Users, Heart, BarChart3, Phone, Sparkles, Award, ArrowRight, MapPin, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import ApiService from '../services/api';

interface AboutUs {
  id: number;
  heroTitle: string;
  heroSubtitle?: string;
  heroDescription?: string;
  heroImage?: string;
  storyTitle: string;
  storyContent?: string;
  storyImage?: string;
  valuesTitle: string;
  valuesContent?: string;
  statsTitle: string;
  statsSubtitle?: string;
  statsData: Array<{ title: string; value: string; icon: string }>;
  ctaTitle: string;
  ctaContent?: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TeamMember {
  id: number;
  name: string;
  specialization: string;
  imageUrl?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const AboutUs: React.FC = () => {
  const [aboutUs, setAboutUs] = useState<AboutUs | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAboutUs = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAboutUs();
      
      // Ensure statsData is properly parsed as an array
      const aboutUsData = response.aboutUs;
      if (aboutUsData && aboutUsData.statsData) {
        // If statsData is a string, parse it; if it's already an array, use it as is
        if (typeof aboutUsData.statsData === 'string') {
          try {
            aboutUsData.statsData = JSON.parse(aboutUsData.statsData);
          } catch (parseError) {
            console.error('Error parsing statsData:', parseError);
            aboutUsData.statsData = [];
          }
        } else if (!Array.isArray(aboutUsData.statsData)) {
          aboutUsData.statsData = [];
        }
      } else {
        aboutUsData.statsData = [];
      }
      
      setAboutUs(aboutUsData);
    } catch (error: any) {
      console.error('About us load error:', error);
      setError('About us page could not be loaded: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await ApiService.getTeamMembers();
      setTeamMembers(response.teamMembers || []);
    } catch (error) {
      console.error('Team members load error:', error);
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  useEffect(() => {
    loadAboutUs();
    loadTeamMembers();
    loadHomeSettings();
    
    // Sayfa yüklendiğinde en üste scroll yap
    window.scrollTo(0, 0);
  }, []);

  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
    }
    if (imageUrl.startsWith('/uploads/about/')) {
      const filename = imageUrl.replace('/uploads/about/', '');
      return `http://localhost:5000/api/proxy/uploads/about/${filename}`;
    }
    return imageUrl;
  };

  const getTeamImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
    }
    if (imageUrl.startsWith('/uploads/team/')) {
      const filename = imageUrl.replace('/uploads/team/', '');
      return `http://localhost:5000/api/proxy/uploads/team/${filename}`;
    }
    return imageUrl;
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error || !aboutUs) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-600 text-red-400 px-4 py-3 rounded max-w-md">
          <p>{error || 'About us page not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 min-h-screen flex items-center">
        {aboutUs.heroImage && (
          <div className="absolute inset-0 z-0">
            <img
              src={getImageUrl(aboutUs.heroImage)}
              alt="Hero Background"
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
        )}
        <div className="relative z-10 px-4">
          <div className="flex justify-start items-center">
            <div className="w-full lg:w-1/2 lg:ml-48">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-light mb-4 sm:mb-6 tracking-wider" style={{ fontFamily: 'NewYork' }}>
                {aboutUs.heroTitle}
              </h1>
              
              {aboutUs.heroSubtitle && (
                <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-4 sm:mb-6 font-light">
                  {aboutUs.heroSubtitle}
                </p>
              )}
              
              {aboutUs.heroDescription && (
                <div className="text-base sm:text-lg md:text-xl text-gray-400 mb-6 sm:mb-8 font-light leading-tight">
                  {aboutUs.heroDescription.split('\n').map((paragraph, index) => (
                    <p key={index} className="mb-2">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>



      {/* Team Members Section */}
      {teamMembers.length > 0 && (
        <section className="py-12 sm:py-16 md:py-20 px-4" style={{ backgroundColor: '#f2eadd' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-light mb-6 sm:mb-8 tracking-wider" style={{ fontFamily: 'NewYork', color: '#9e906d' }}>
                Ekip Üyelerimiz
              </h2>
              <p className="text-lg sm:text-xl font-light max-w-3xl mx-auto leading-relaxed px-4" style={{ color: '#000000', fontFamily: 'Montserrat-Light' }}>
                Her biri alanında yılların tecrübesine sahip, profesyonel ve tutkulu ekibimiz sizlere en iyi hizmeti sunmak için burada… Bilgi, deneyim ve sanatın birleştiği bu yolculukta; size sadece hizmet değil, aynı zamanda güven ve memnuniyet vadediyoruz 🤎 Siz hayal edin, biz uzmanlığımızla gerçeğe dönüştürelim!
              </p>
            </div>

                                      <div className="flex flex-wrap justify-center gap-8">
               {teamMembers.map((member) => (
                 <div key={member.id} className="text-center group flex-shrink-0">
                                      <div className="relative mb-6">
                                                 <div className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 mx-auto overflow-hidden border-4 border-gray-700 group-hover:border-gray-500 transition-all duration-300">
                       <img
                         src={getTeamImageUrl(member.imageUrl)}
                         alt={member.name}
                         className="w-full h-full object-cover"
                         onError={(e) => {
                           const target = e.target as HTMLImageElement;
                           target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
                         }}
                       />
                     </div>
                   </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-light mb-2 tracking-wide" style={{ fontFamily: 'NewYork', color: '#9e906d' }}>
                    {member.name}
                  </h3>
                  <p className="font-light" style={{ color: '#000000', fontFamily: 'Montserrat-Light' }}>
                    {member.specialization}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}



      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-4xl mx-auto text-center">
                      <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-white mb-6 tracking-wider" style={{ fontFamily: 'Montserrat-Regular' }}>
              {aboutUs.ctaTitle}
            </h3>
          <div className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 font-light space-y-3 sm:space-y-4">
            <p className="flex items-center justify-center gap-2">
              📍 Melikşah, Akkonak Sk. As Plaza D:1. Kat No:3, 42090 Meram/Konya
            </p>
            <p className="flex items-center justify-center gap-2">
              💌 info@shevastudiokonya.com
            </p>
            <p className="flex items-center justify-center gap-2">
              📞 0554 989 46 63
            </p>
          </div>
                      <div className="flex justify-center">
                          <Link 
                to={aboutUs.ctaButtonLink}
                className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 text-base sm:text-lg tracking-wide"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {aboutUs.ctaButtonText}
              </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
